import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { getPillarKpiBreakdown } from '../api';
import PageChrome from '../components/PageChrome';

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: '28day', label: '28-Day' },
  { value: 'preseason', label: 'Pre-season' },
  { value: 'season', label: 'Season' },
];

// Same two pillars, different KPI sets and data source (kpi_definitions.pillar) — one shared
// component rather than two page implementations (Stage 6a, 2026-07-23). Accent colors match
// TrainerRatings.js's existing Tactical/Identity column headers for visual consistency with
// where this data is entered.
const PILLAR_CONFIG = {
  tactical: { label: 'Tactical', accent: '#3b82f6' },
  identity: { label: 'Identity', accent: '#8b5cf6' },
};

// Same NULL-to-bottom-regardless-of-direction comparator as Team360Table.js (Stage 4) — not
// Availability.js's SeasonAnalysisTab comparator, which uses `?? -1` and only sorts nulls
// last when descending (confirmed bug while investigating this stage). Each sortable table in
// this codebase keeps its own copy rather than a shared util (existing convention).
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}

function SortHeader({ label, sortKey, activeKey, dir, onClick, align }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(sortKey)} style={{ textAlign: align || 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: active ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>{label} <Icon size={11} /></span>
    </th>
  );
}

const numCell = { padding: '10px 14px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' };

function ScoreCell({ value, bold }) {
  if (value == null) return <td style={{ ...numCell, color: 'var(--text-dimmed)' }}>no data</td>;
  return <td style={{ ...numCell, fontWeight: bold ? 700 : 400 }}>{Math.round(value)}</td>;
}

export default function TeamKpiOverview({ pillar, user, onNavigate }) {
  const config = PILLAR_CONFIG[pillar];
  const [period, setPeriod] = useState('week');
  const [rows, setRows] = useState([]);
  const [kpiCols, setKpiCols] = useState([]); // [{ kpiId, kpiName }]
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('pillarScore');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    setLoading(true);
    getPillarKpiBreakdown(pillar, period)
      .then(data => {
        const list = data || [];
        setKpiCols(list[0]?.kpis?.map(k => ({ kpiId: k.kpiId, kpiName: k.kpiName })) || []);
        setRows(list.map(r => {
          const flat = { player_id: r.player_id, name: r.name, position: r.position, pillarScore: r.pillarScore };
          r.kpis.forEach(k => { flat[`kpi_${k.kpiId}`] = k.value; });
          return flat;
        }));
      })
      .catch(() => { setRows([]); setKpiCols([]); })
      .finally(() => setLoading(false));
  }, [pillar, period]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [rows, sortKey, sortDir]
  );

  const columnCount = 3 + kpiCols.length;

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
    <div style={{ maxWidth: 1100 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: config.accent }}>360 View — {config.label} Team Overview</h2>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 32px' }}>
        Each KPI is a 65% match / 35% training blend (Stage 3). "no data" means nothing qualified in this window — not a score of zero. A player with no {config.label.toLowerCase()} data at all shows the whole row as no data.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {PERIODS.map(p => (
          <button key={p.value} onClick={() => setPeriod(p.value)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${period === p.value ? config.accent : 'var(--border-strong)'}`, background: period === p.value ? config.accent : 'transparent', color: period === p.value ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {p.label}
          </button>
        ))}
      </div>

      {loading && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>}

      {!loading && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-strong)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)' }}>
                <SortHeader label="Player" sortKey="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Position" sortKey="position" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label={`${config.label} Score`} sortKey="pillarScore" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                {kpiCols.map(k => (
                  <SortHeader key={k.kpiId} label={k.kpiName} sortKey={`kpi_${k.kpiId}`} activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => (
                <tr key={r.player_id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{r.name}</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>{r.position || '—'}</td>
                  <ScoreCell value={r.pillarScore} bold />
                  {kpiCols.map(k => <ScoreCell key={k.kpiId} value={r[`kpi_${k.kpiId}`]} />)}
                </tr>
              ))}
              {!sortedRows.length && (
                <tr><td colSpan={columnCount} style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 13 }}>No active players found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </PageChrome>
  );
}
